<template>
    <BaseDialog :open="isOpen" :title="errorMessage.title || 'Select microphone'" @update:open="handleDialogOpenChange">
        <div id="recording-test-dialog" data-testid="recording-test-dialog" class="flex flex-col gap-4 text-center items-center text-ink">
            <template v-if="error">
                <p class="body-small max-w-xs text-center text-ink" v-text="errorMessage.body"></p>
            </template>
            <div v-if="loading" class="text-ink-muted flex justify-center p-4">
                <div class="h-6 w-6"><LoadingSpinner /></div>
            </div>
            <div v-show="!loading && shouldShowMicTest" class="flex flex-col gap-4 w-full min-w-0">
                <p class="max-w-sm mx-auto body">Can you confirm that you&apos;re using the correct mic. Speak and pause. Do you hear a replay?</p>
                <div class="flex flex-col gap-2 mt-4">
                    <label for="microphone-select" class="w-full body-small text-control-label">
                        <div class="flex items-center justify-between w-full">
                            Select microphone <span>{{ testingLabel }}</span>
                        </div>
                        <BaseSelect id="microphone-select" v-model="micSelection" :options="mics.map((mic) => ({ key: mic.deviceId, label: mic.label }))" name="microphoneSelect" />
                    </label>
                </div>
                <div class="flex flex-col justify-start text-left gap-2">
                    <div class="body-small text-ink-muted">Input level</div>
                    <div role="presentation" class="flex flex-row space-between gap-2">
                        <template v-for="(item, idx) in levelSignifiers" :key="idx">
                            <div class="h-5 w-2 rounded-lg" :class="idx < audioLevel ? 'bg-ink' : 'bg-border-strong'"></div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <template #footer>
            <BaseButton theme="tertiary" size="sm" @click="cancel">{{ dismissText }}</BaseButton>
            <BaseButton v-if="!loading && shouldShowMicTest" theme="primary" @click="confirmMicSelection">Use this Mic</BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup>
import { app } from "@microsoft/teams-js";
import { computedAsync } from "@vueuse/core";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import { isInIFrame } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

import BaseButton from "./components/BaseButton.vue";
import { CHAT_EVENT } from "./events";
import LoadingSpinner from "./icons/LoadingSpinner.vue";

const ERRORS = {
    NO_INPUT_DEVICES: "no_devices",
    NOT_FOUND: "not_found",
    NOT_ALLOWED: "not_allowed",
    CATCH_ALL: "catch_all",
};
const PREFERRED_MIC_STORAGE_KEY = "valence_preferred_microphone";
// How many audio level signifiers to show in the UI
const LEVELS_SIGNIFIERS = 25;
const PSEUDO_MIC_DEVICE_IDS = new Set(["default", "communications"]);

function normalizeMics(devices) {
    const audioInputs = devices.filter((device) => device.kind === "audioinput" && device.deviceId !== "");
    const concreteMics = audioInputs.filter((device) => !PSEUDO_MIC_DEVICE_IDS.has(device.deviceId));
    return concreteMics.length > 0 ? concreteMics : audioInputs;
}

const props = defineProps({
    eventSlug: {
        type: String,
        default: undefined,
    },
    dismissText: {
        type: String,
        default: "Use text instead",
    },
});
const emit = defineEmits(["micSelected", "dismiss"]);
const { emitter } = inject("globalProperties");

const loading = ref(true);
const mics = ref([]);
const micSelection = ref(undefined);
const audioOutputSupported = ref(false);
const error = ref(null);
const audioLevel = ref(0);
const isOpen = ref(false);
let pollingIntervalId = null;
let playback = null;
// Media requests cannot be aborted; versions prevent late results from owning another session or device's resources.
let sessionVersion = 0;
let playbackVersion = 0;
let enumerationVersion = 0;

// Creates an Array with N elements, where N = LEVELS_SIGNIFIERS.
// Just used to loop it in the template to draw the signifiers
const levelSignifiers = computed(() => Array.apply(null, Array(LEVELS_SIGNIFIERS)));

const DEFAULT_ERROR_MESSAGE = {
    title: "I'm having trouble hearing you.",
    body: "It looks like your microphone permissions are not turned on in your browser. Please turn them on and try again.",
};
const ERROR_MESSAGES = {
    Office: {
        title: "M365 Copilot does not support microphone access",
        body: "Continue with text only.",
    },
    Outlook: {
        title: "Outlook does not support microphone access",
        body: "Continue with text only.",
    },
    TeamsModern: {
        title: DEFAULT_ERROR_MESSAGE.title,
        body: "It looks like your microphone permissions are not turned on in MS Teams. Please turn them on and try again.",
    },
    [ERRORS.NO_INPUT_DEVICES]: {
        title: DEFAULT_ERROR_MESSAGE.title,
        body: "It looks like there is no microphone connected. Please connect a microphone and try again",
    },
    [ERRORS.NOT_ALLOWED]: DEFAULT_ERROR_MESSAGE,
    [ERRORS.CATCH_ALL]: DEFAULT_ERROR_MESSAGE,
};
const errorMessage = computedAsync(
    async () => {
        const inIFrame = isInIFrame();
        let errorMsgKey = error.value;
        if (inIFrame) {
            app.initialize();
            const context = await app.getContext();
            errorMsgKey = context.app.host.name;
        }
        return ERROR_MESSAGES[errorMsgKey] || DEFAULT_ERROR_MESSAGE;
    },
    { title: null, body: null },
);
const shouldShowMicTest = computed(() => {
    return error.value === null || ![ERRORS.NO_INPUT_DEVICES, ERRORS.NOT_ALLOWED, ERRORS.NOT_FOUND].includes(error.value);
});
const testingLabel = computed(() => {
    const index = mics.value.findIndex((mic) => mic.deviceId === micSelection.value);
    return `${index + 1}/${mics.value.length}`;
});

watch(micSelection, (deviceId) => {
    // Permission acquisition clears the selection before choosing a device.
    if (!deviceId || !isOpen.value) {
        return;
    }
    void createAudioPlayback(deviceId, sessionVersion, ++playbackVersion);
});

onMounted(() => {
    emitter.on(CHAT_EVENT.OPEN_MIC_TEST_DIALOG, open);
});

onUnmounted(() => {
    dismiss();
    emitter.off(CHAT_EVENT.OPEN_MIC_TEST_DIALOG, open);
});

function isCurrentSession(version) {
    return isOpen.value && version === sessionVersion;
}

function stopStream(stream) {
    stream.getTracks().forEach((track) => track.stop());
}

async function start(version) {
    // Enumerating the mics requires having permissions to access the media devices so we prompt once first. This is a noop if the user has already granted permission elsewhere before.
    try {
        const permissionStream = await requestPermission({ audio: true });
        stopStream(permissionStream);
        if (!isCurrentSession(version)) {
            return;
        }
        const refreshed = await setDeviceInformation(version);
        if (!refreshed || !isCurrentSession(version)) {
            return;
        }
        // Query for mics at an interval to pick up any microphone that gets connected or disconnected.
        pollingIntervalId = setInterval(() => {
            void setDeviceInformation(version);
        }, 2000);
    } catch (e) {
        if (!isCurrentSession(version)) {
            return;
        }
        handleMediaDeviceError(e);
    }
    if (!isCurrentSession(version)) {
        return;
    }
    loading.value = false;
    await nextTick();
    if (isCurrentSession(version) && shouldShowMicTest.value) {
        document.getElementById("microphone-select")?.focus();
    }
}

function handleMediaDeviceError(e) {
    switch (e.name) {
        case "NotAllowedError":
            error.value = ERRORS.NOT_ALLOWED;
            return;
        case "NotFoundError":
            error.value = ERRORS.NOT_FOUND;
            return;
    }
    error.value = ERRORS.CATCH_ALL;
}

async function setDeviceInformation(version) {
    if (!isCurrentSession(version)) {
        return false;
    }
    const requestVersion = ++enumerationVersion;
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!isCurrentSession(version) || requestVersion !== enumerationVersion) {
            return false;
        }
        mics.value = normalizeMics(devices);
        audioOutputSupported.value = devices.some((device) => device.kind === "audiooutput");
        if (mics.value.length === 0) {
            error.value = ERRORS.NO_INPUT_DEVICES;
            return true;
        } else {
            error.value = null;
        }

        // Default to saved preference, or first audio input if no selection has been made,
        // or if a selection exists for a mic that no longer exists.
        if (!micSelection.value || !mics.value.find((mic) => mic.deviceId === micSelection.value)) {
            const savedMicId = localStorage.getItem(PREFERRED_MIC_STORAGE_KEY);
            if (savedMicId && mics.value.find((mic) => mic.deviceId === savedMicId)) {
                micSelection.value = savedMicId;
            } else {
                micSelection.value = mics.value[0].deviceId;
            }
        }
    } catch (e) {
        if (!isCurrentSession(version) || requestVersion !== enumerationVersion) {
            return false;
        }
        handleMediaDeviceError(e);
    }
    return true;
}

function requestPermission(constraints = {}) {
    return navigator.mediaDevices.getUserMedia(constraints);
}

function dismiss() {
    isOpen.value = false;
    sessionVersion++;
    playbackVersion++;
    enumerationVersion++;
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
    void teardownPlayback();
}

function handleDialogOpenChange(open) {
    if (!open && isOpen.value) {
        cancel();
    }
}

function cancel() {
    if (!isOpen.value) {
        return;
    }
    dismiss();
    logUserInteraction("choose_mic_modal_text_selected", {}, props.eventSlug);
    emit("dismiss");
}

function confirmMicSelection() {
    const mic = mics.value.find((mic) => mic.deviceId === micSelection.value);
    if (!isOpen.value || !mic) {
        return;
    }
    localStorage.setItem(PREFERRED_MIC_STORAGE_KEY, micSelection.value);
    dismiss();
    logUserInteraction("choose_mic_modal_mic_selected", {}, props.eventSlug);
    emit("micSelected", mic);
}

async function disposePlayback(resources) {
    if (resources.disposed) {
        return;
    }
    resources.disposed = true;
    stopStream(resources.stream);
    resources.nodes.forEach((node) => node.disconnect());
    if (resources.animationFrameId !== null) {
        cancelAnimationFrame(resources.animationFrameId);
    }
    await resources.context.close();
}

async function teardownPlayback() {
    const ownedPlayback = playback;
    // Detach before closing asynchronously, so completion cannot clear a newer capture.
    playback = null;
    if (ownedPlayback) {
        await disposePlayback(ownedPlayback);
    }
}

function open() {
    if (isOpen.value) {
        return;
    }
    const version = ++sessionVersion;
    micSelection.value = undefined;
    mics.value = [];
    error.value = null;
    audioOutputSupported.value = false;
    audioLevel.value = 0;
    loading.value = true;
    isOpen.value = true;
    void start(version);
    logUserInteraction("choose_mic_modal_viewed", {}, props.eventSlug);
}

async function createAudioPlayback(deviceId, version, requestVersion) {
    const isCurrent = () => isCurrentSession(version) && requestVersion === playbackVersion && micSelection.value === deviceId;
    let stream;
    let resources;
    try {
        if (!isCurrent()) {
            return;
        }
        await teardownPlayback();
        if (!isCurrent()) {
            return;
        }
        stream = await requestPermission({ audio: { deviceId } });
        if (!isCurrent()) {
            stopStream(stream);
            return;
        }
        resources = { stream, context: new AudioContext(), nodes: [], animationFrameId: null, disposed: false };
        const source = resources.context.createMediaStreamSource(stream);
        resources.nodes.push(source);
        const delay = resources.context.createDelay();
        delay.delayTime.value = 300;
        resources.nodes.push(delay);
        const analyser = resources.context.createAnalyser();
        analyser.fftSize = 256;
        resources.nodes.push(analyser);
        const bufferLength = analyser.frequencyBinCount;
        const levelsArray = new Uint8Array(bufferLength);
        source.connect(analyser);
        if (audioOutputSupported.value) {
            source.connect(delay);
            delay.connect(resources.context.destination);
            logUserInteraction("choose_mic_audio_playback", {}, props.eventSlug);
        }
        playback = resources;
        const getAudioLevels = () => {
            if (!isCurrent() || playback !== resources || resources.disposed) {
                return;
            }
            analyser.getByteFrequencyData(levelsArray);

            // Calculate the average of the frequency data
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                sum += levelsArray[i];
            }
            const average = sum / bufferLength;
            audioLevel.value = Math.round((average / 255) * LEVELS_SIGNIFIERS);
            // Run at levels analysis on a browser-controlled loop
            resources.animationFrameId = requestAnimationFrame(getAudioLevels);
        };
        getAudioLevels();
    } catch (e) {
        if (resources) {
            if (playback === resources) {
                playback = null;
            }
            await disposePlayback(resources);
        } else if (stream) {
            stopStream(stream);
        }
        if (isCurrent()) {
            handleMediaDeviceError(e);
        }
    }
}
</script>
